import { lazy, Suspense, useEffect, useState } from 'react';
import GoogleMap, { GoogleConfig, loadGoogle, onGoogleRefused } from './GoogleMap';
import { Props } from './mapCommon';

export type { MapRoute, MapVehicle } from './mapCommon';

// Google Maps (decision 9C) when the server hands us a browser key (logged in only), else the
// OpenStreetMap map, also if Google's script can't load or the key is refused. MapLibre is only
// downloaded when it's needed.
const OsmMap = lazy(() => import('./OsmMap'));

type Engine = { google: GoogleConfig } | 'osm' | null;
let known: Engine = null;
let asking: Promise<Engine> | null = null;
const pick = () => (asking ??= fetch('/api/map/config', { credentials: 'same-origin' })
  .then((r) => (r.ok ? r.json() : { google: false }))
  .then(async (d: { google?: boolean; key?: string; mapId?: string }): Promise<Engine> => {
    if (!d.google || !d.key || !d.mapId) return 'osm';
    const cfg = { key: d.key, mapId: d.mapId };
    await loadGoogle(cfg);
    return { google: cfg };
  })
  .catch((): Engine => 'osm')
  .then((e) => (known = e)));

const blank = <div className="map" role="region" aria-label="Map" />;

// Which map engine this page uses (null while finding out); the geofence editor needs Google.
export function useMapEngine(): Engine {
  const [engine, setEngine] = useState(known);
  useEffect(() => { if (engine === null) void pick().then(setEngine); }, [engine]);
  return engine;
}

export function FleetMap(props: Props) {
  const [engine, setEngine] = useState(known);
  useEffect(() => { if (engine === null) void pick().then(setEngine); }, [engine]);
  useEffect(() => {
    onGoogleRefused(() => { known = 'osm'; setEngine('osm'); });
    return () => onGoogleRefused(null);
  }, []);
  if (engine === null) return blank;
  if (engine === 'osm') return <Suspense fallback={blank}><OsmMap {...props} /></Suspense>;
  return <GoogleMap {...props} cfg={engine.google} />;
}
